import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import useSites from "../../data/useSites";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ISiteSummary } from "../../models/Site";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string;
  shortCode: string;
  isDefault: boolean;
};

interface SitesModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  site?: ISiteSummary;
}

export default function SitesModal({ open, setOpen, site }: SitesModalProps) {
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Site");
  const { create, update, remove } = useSites();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset } = useForm<FormValues>();

  // A site can only be deleted once nothing is at it - the API refuses otherwise, so say so up front.
  const hasPlaces = (site?.yardLocationCount ?? 0) + (site?.binCount ?? 0) > 0;

  const onSubmit = (data: FormValues) => {
    const request = {
      name: data.name,
      shortCode: data.shortCode === "" ? null : data.shortCode,
      isDefault: data.isDefault ?? false,
    };

    const handlers = {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
        reset({});
      },
      onError: saveHandlers.onError,
    };

    if (site !== undefined) {
      update.mutate({ siteId: site.id, site: request }, handlers);
    } else {
      create.mutate(request, handlers);
    }
  };

  const handleDelete = () => {
    remove.mutate(site?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError,
    });
  };

  useEffect(() => {
    if (site !== undefined) {
      reset({
        name: site.name,
        shortCode: site.shortCode ?? "",
        isDefault: site.isDefault,
      });
    } else {
      reset({ name: "", shortCode: "", isDefault: false });
    }
  }, [site, open, reset]);

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title="Delete Site"
        message={"Are you sure you want to delete this site?\n This action cannot be undone."}
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isLoading={remove.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{site ? "Edit a Site" : "Add a Site"}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)}>
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} />

              <TextInput label="Short Code" registerName="shortCode" register={register} />

              <CheckboxInput label="Default site" registerName="isDefault" register={register} />

              <ButtonInput label={site ? "Save Changes" : "Create"} isSubmit={true} onClick={undefined} classes={undefined} isLoading={create.isLoading || update.isLoading} />
              {site !== undefined && (
                <div>
                  <ButtonInput
                    label="Delete"
                    isSubmit={false}
                    onClick={() => setDeleteOpen(true)}
                    classes={"!bg-red-500 hover:!bg-red-600"}
                    disabled={hasPlaces}
                  />
                  {hasPlaces && (
                    <p className="mt-2 text-sm text-gray-500">
                      Unassign its yard and warehouse locations before deleting this site.
                    </p>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
